import React, { useEffect } from "react";
import { Play, ArrowRight, CalendarCheck } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { REPLAY, EVENT, ASSETS, GHL_CALENDAR } from "../mock";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const Replay = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    // Load GHL embed script for calendar iframe auto-resize
    const script = document.createElement("script");
    script.src = "https://link.msgsndr.com/js/form_embed.js";
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <div className="bg-[#070b16] min-h-screen" data-testid="replay-page">
      <Navbar />
      <section className="relative overflow-hidden pt-36 pb-16">
        <div className="absolute inset-0 bg-cover bg-center opacity-[0.1]" style={{ backgroundImage: `url(${ASSETS.blueprint})` }} />
        <div className="absolute inset-0 blueprint-grid opacity-20" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <p className="font-mono text-[10px] tracking-[0.35em] text-[#6f7d9f] uppercase mb-4">{REPLAY.eyebrow}</p>
          <h1 className="font-heading uppercase text-white text-3xl sm:text-5xl leading-[0.98] tracking-tight" data-testid="replay-headline">
            Missed the live session, or want to watch it again? <span className="text-[#6b9be8]">It's below.</span>
          </h1>
        </div>
      </section>

      <section className="pb-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="relative aspect-video border border-[#22304e] bg-[#0c1324] flex flex-col items-center justify-center gap-4" data-testid="replay-video">
            <div className="w-16 h-16 rounded-full border border-[#3f639f] flex items-center justify-center">
              <Play size={22} className="text-[#6b9be8] ml-1" />
            </div>
            <p className="font-heading uppercase text-white text-xl tracking-tight">{REPLAY.videoPlaceholder}</p>
            <p className="text-[#7f8db0] text-sm">{REPLAY.videoCaption}</p>
          </div>
        </div>
      </section>

      <section className="py-16 border-t border-[#131c31]">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-heading uppercase text-white text-3xl sm:text-4xl tracking-tight mb-8">{REPLAY.recapTitle}</h2>
          <div className="space-y-5">
            {REPLAY.recap.map((r) => (
              <div key={r} className="flex items-start gap-4 border-b border-[#1c2740] pb-5">
                <ArrowRight size={18} className="text-[#6b9be8] mt-0.5 shrink-0" />
                <p className="text-[#dfe6f2] text-sm sm:text-base">{r}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 border-t border-[#131c31]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid md:grid-cols-[0.62fr_0.38fr] gap-10 items-start mb-10">
            <div>
              <h2 className="font-heading uppercase text-white text-3xl sm:text-4xl tracking-tight mb-5">{REPLAY.ctaTitle}</h2>
              <p className="text-[#aeb9d4] leading-relaxed text-sm sm:text-base mb-4">{REPLAY.ctaBody}</p>
              <p className="flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] text-[#6b9be8] uppercase">
                <CalendarCheck size={14} /> Pick a time below — it takes 30 seconds
              </p>
              <p className="mt-4 text-sm text-[#7f8db0]">
                Or email me directly — <a href={`mailto:${EVENT.email}`} className="text-[#9db8e8] underline hover:text-white">{EVENT.email}</a>
              </p>
            </div>
            <div className="card-hover border border-[#1c2740] bg-[#0c1324]/60 p-7">
              <img src={ASSETS.oscar} alt="Oscar Tobar" className="w-20 h-20 object-cover border border-[#22304e] mb-4" />
              <p className="text-white font-semibold">{EVENT.host}</p>
              <p className="text-[#6b9be8] text-xs mb-3">{REPLAY.hostTitle}</p>
              <p className="text-[#8fa0c0] text-sm leading-relaxed">{REPLAY.hostNote}</p>
            </div>
          </div>
          <div className="border border-[#22304e] bg-white rounded-md overflow-hidden" data-testid="replay-ghl-calendar">
            <iframe
              src={GHL_CALENDAR.embedUrl}
              title={GHL_CALENDAR.name}
              id={`ghl-cal-${GHL_CALENDAR.id}`}
              style={{ width: "100%", border: "none", overflow: "hidden", minHeight: "700px" }}
              scrolling="no"
            />
          </div>
        </div>
      </section>

      <section className="py-16 border-t border-[#131c31] pb-24">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-heading uppercase text-white text-3xl sm:text-4xl tracking-tight mb-8">{REPLAY.faqTitle}</h2>
          <Accordion type="single" collapsible data-testid="replay-faq">
            {REPLAY.faq.map((f, i) => (
              <AccordionItem key={i} value={`rfaq-${i}`} className="border-[#1c2740]">
                <AccordionTrigger className="hover:no-underline py-5 group">
                  <span className="flex items-center gap-5 text-left">
                    <span className="font-mono text-xs text-[#6b9be8]">{String(i + 1).padStart(2, "0")}</span>
                    <span className="font-heading uppercase text-white text-base sm:text-lg tracking-tight group-hover:text-[#9db8e8] transition-colors">{f.q}</span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-[#8fa0c0] text-sm leading-relaxed pl-10 pb-5">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Replay;
